import React, { useState, ReactNode, PropsWithChildren } from 'react';
import ObjectBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ObjectBuilder';
import _ from 'lodash';
import { ObjectSchema } from '@scale/shared/taskTemplate/types';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { ChunkColor } from 'react-scale/dashboard.scale.com/components/SelfServe/Onboarding/Task/components/TaxonomyEditor';
import { TextCollectionField } from '@scale/shared/types/params/textcollection';
import { TypeBuilderMapType } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemasType';

interface ArrayBuilderProps<E> {
  elements: Array<E>;
  setElements: (arg: Array<E>) => void;
  getKey: (element: E) => string;
  getTitle: (element: E, index: number) => string;
  getSchema: (element: E) => ObjectSchema;
  getIsHidden?: (element: E) => boolean;
  getTags?: (element: E, index: number) => Array<string | undefined> | undefined;
  labelsToColors?: Record<string, ChunkColor>;
  toggleLabel?: (l: string) => void;
  fullWidth?: boolean;
  restrictFields?: boolean; // only allow deleting, no editing or duplicating
  hideVisibilityCondition?: (element: E) => boolean;
  taskTemplateParams?: Record<string, any>;
  fields?: Array<TextCollectionField>;
  delimiter?: ReactNode;
  getShowError?: (element: E, index: number) => boolean;
  getCloneFn?: (element: E) => E;
  getWarningOnModify?: (propertyName: string) => string | undefined; // if specified, warning message when modifying an element
  typeBuilderMap: TypeBuilderMapType;
}

function ArrayBuilder<T>(props: PropsWithChildren<ArrayBuilderProps<T>>) {
  const {
    elements,
    setElements,
    getKey,
    getTitle,
    getSchema,
    getIsHidden,
    toggleLabel,
    fullWidth,
    getTags,
    restrictFields,
    hideVisibilityCondition,
    taskTemplateParams,
    fields,
    delimiter,
    getShowError,
    getCloneFn,
    getWarningOnModify,
    typeBuilderMap,
  } = props;
  const [openBuilders, setOpenBuilders] = useState<boolean[]>(elements.map(_ => false));
  const setElement = (index: number, newElement: T) => {
    const newElements = [...elements];
    newElements[index] = newElement;
    setElements(newElements);
  };
  const removeElement = (index: number) => {
    const newElements = [...elements];
    newElements.splice(index, 1);
    setElements(newElements);
    const newOpenBuilders = [...openBuilders];
    newOpenBuilders.splice(index, 1);
    setOpenBuilders(newOpenBuilders);
  };
  const cloneElement = (index: number) => {
    const currentlyOpen = openBuilders[index];
    const newElements = [...elements];
    const clonedElement = getCloneFn ? getCloneFn(elements[index]) : _.cloneDeep(elements[index]);
    newElements.splice(index + 1, 0, clonedElement);
    setElements(newElements);
    const newOpenBuilders = [...openBuilders];
    newOpenBuilders.splice(index + 1, 0, currentlyOpen);
    setOpenBuilders(newOpenBuilders);
  };
  const handleReorder = (result: DropResult) => {
    if (result.destination) {
      const newElements = [...elements];
      const [removedElement] = newElements.splice(result.source.index, 1);
      newElements.splice(result.destination.index, 0, removedElement);
      setElements(newElements);
      const newOpenBuilders = [...openBuilders];
      const [removedBuilder] = newOpenBuilders.splice(result.source.index, 1);
      newOpenBuilders.splice(result.destination.index, 0, removedBuilder);
      setOpenBuilders(newOpenBuilders);
    }
  };

  const visibleElements = hideVisibilityCondition
    ? elements.filter(e => !hideVisibilityCondition(e))
    : elements;

  return (
    <DragDropContext onDragEnd={handleReorder}>
      <Droppable droppableId="droppable">
        {provided => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {visibleElements.map((element, i) => (
              <div key={i}>
                <Draggable draggableId={i.toString()} index={i}>
                  {provided => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                      }}
                    >
                      <ObjectBuilder
                        typeBuilderMap={typeBuilderMap}
                        key={getKey(element) + i}
                        title={getTitle(element, i)}
                        object={element}
                        isHidden={getIsHidden ? getIsHidden(element) : false}
                        schema={getSchema(element)}
                        updateObject={(newElement: T) => setElement(i, newElement)}
                        deleteObject={() => removeElement(i)}
                        cloneObject={() => cloneElement(i)}
                        dragHandleProps={provided.dragHandleProps}
                        labelColor={
                          props.labelsToColors ? props.labelsToColors[getKey(element)] : undefined
                        }
                        toggleLabel={toggleLabel ? () => toggleLabel(getKey(element)) : undefined}
                        fullWidth={fullWidth}
                        tags={getTags ? getTags(element, i) : undefined}
                        showBuilder={openBuilders[i]}
                        toggleShowBuilder={() => {
                          const newOpenBuilders = [...openBuilders];
                          newOpenBuilders[i] = !openBuilders[i];
                          setOpenBuilders(newOpenBuilders);
                        }}
                        restrictFields={restrictFields}
                        taskTemplateParams={taskTemplateParams}
                        fields={fields}
                        showError={getShowError ? getShowError(element, i) : false}
                        getWarningOnModify={getWarningOnModify}
                      />
                    </div>
                  )}
                </Draggable>
                {i < visibleElements.length - 1 && !_.isNil(delimiter) && (
                  <div className="w-full flex justify-center mb-2 pb-2">{delimiter}</div>
                )}
              </div>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default ArrayBuilder;
